import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Radar } from "lucide-react";
import SellerIdentity from "@/components/SellerIdentity";


const LAST_UPDATED = "5 September 2026";

const Terms = () => (
  <>
    <Helmet>
      <title>Terms of Service — Trends Discovery</title>
      <meta
        name="description"
        content="The terms that govern your $39 lifetime Trends Discovery license: what's included, refunds, acceptable use and liability."
      />
      <link rel="canonical" href="https://trendsdiscovery.com/terms" />
      <meta property="og:title" content="Terms of Service — Trends Discovery" />
      <meta
        property="og:description"
        content="The terms that govern your $39 lifetime Trends Discovery license."
      />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://trendsdiscovery.com/terms" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    <main className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> BACK TO HOME
        </Link>

        <div className="mt-8 flex items-center gap-2">
          <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
            <Radar className="w-4 h-4" />
          </span>
          <span className="font-display font-semibold">Trends.Discovery</span>
        </div>

        <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Terms of Service</h1>
        <p className="mt-3 font-mono text-xs text-muted-foreground">
          LAST UPDATED: {LAST_UPDATED}
        </p>

        <div className="mt-10 space-y-10 text-sm leading-relaxed text-muted-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              1. Agreement
            </h2>
            <p>
              These terms form a binding agreement between you and Trends Discovery ("we", "us")
              covering your use of trendsdiscovery.com and the products available on it. By creating
              an account or purchasing a license you accept these terms. If you do not agree, please
              do not use the service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              2. Your license
            </h2>
            <p>
              The Lifetime License is a single payment of <strong>$39 USD</strong> (one-time, no
              recurring charges). It grants one individual a personal, non-exclusive,
              non-transferable right to access the trend feed, launch blueprints, exports and every
              feature we later add to the core product, for as long as we operate the service.
            </p>
            <p className="mt-3">
              Licenses are issued to a single person. Sharing account credentials, reselling access,
              or redistributing blueprints as your own commercial data product is not permitted.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              3. Payment and refunds
            </h2>
            <p>
              Payments are processed by our payment provider; we never store your full card details.
              Your license activates automatically once the payment is confirmed. If your license
              does not appear in your dashboard within a few minutes, contact support and we will
              resolve it manually.
            </p>
            <p className="mt-3">
              You may request a full refund within <strong>14 days</strong> of purchase for any
              reason. Email us from the address on your account and we will refund the $39 without
              questions. Refunded licenses are deactivated.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              4. Acceptable use
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Do not scrape, mirror or bulk-export the feed through automated means.</li>
              <li>Do not attempt to bypass access controls, rate limits or authentication.</li>
              <li>Do not upload unlawful content or use the service to harass others.</li>
              <li>Do not share your login with people who do not hold their own license.</li>
            </ul>
            <p className="mt-3">
              We may suspend or terminate an account that materially breaches these rules. Where a
              breach is not deliberate we will contact you first.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              5. No guarantee of results
            </h2>
            <p>
              Trends Discovery provides research, scoring and planning material. It is not business,
              financial, legal or investment advice, and we do not guarantee sales, profit or the
              accuracy or completeness of any trend, score, supplier suggestion or blueprint. All
              commercial decisions you take remain yours.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              6. Availability and changes
            </h2>
            <p>
              We aim for continuous availability but do not promise uninterrupted service.
              Features, data sources and scoring methods evolve. If we ever have to discontinue the
              service entirely, we will give reasonable notice and let you export your saved
              blueprints.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              7. Intellectual property
            </h2>
            <p>
              The platform, its interface, scoring models and generated blueprint formats remain our
              property. You keep ownership of anything you create using them, including products you
              launch and content you write.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              8. Limitation of liability
            </h2>
            <p>
              To the maximum extent permitted by law, our total liability arising from the service is
              limited to the amount you paid for your license. We are not liable for lost profits,
              lost opportunities or indirect damages. Nothing here limits liability that cannot be
              limited by law.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              9. Changes to these terms
            </h2>
            <p>
              We may update these terms as the product changes. Material changes will be announced in
              the app or by email, and the date at the top of this page will be updated. Continuing
              to use the service after a change means you accept the revised terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              10. Contact
            </h2>
            <p>
              Questions about these terms, a refund, or your license? Reach us through the{" "}
              <Link to="/support" className="text-primary hover:underline">
                support page
              </Link>{" "}
              or email{" "}
              <a
                href="mailto:info@skaiboss.com"
                className="text-primary hover:underline"
              >
                info@skaiboss.com
              </a>
              . See also our{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
            <SellerIdentity className="mt-6" />
          </section>

        </div>
      </div>
    </main>
  </>
);

export default Terms;
